import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  alpha,
  Stack,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import PolicyIcon from '@mui/icons-material/Policy';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import PersonIcon from '@mui/icons-material/Person';
import StorageIcon from '@mui/icons-material/Storage';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import UpdateIcon from '@mui/icons-material/Update';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CookieIcon from '@mui/icons-material/Cookie';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const PoliticaPrivacidadePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const sections = [
    {
      id: 'introducao',
      title: 'Introdução',
      icon: PolicyIcon,
      content: `Esta Política de Privacidade descreve como a ACUTIS TECNOLOGIA LTDA ("nós", "nossa" ou "empresa") coleta, usa, armazena e protege suas informações pessoais quando você utiliza nossas plataformas digitais, incluindo o Meu Sacramento.

Estamos comprometidos em proteger sua privacidade e garantir a transparência sobre como tratamos seus dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018) e demais legislações aplicáveis.

Ao utilizar nossos serviços, você concorda com as práticas descritas nesta política. Recomendamos que leia atentamente este documento para compreender como seus dados são tratados.`
    },
    {
      id: 'dados-coletados',
      title: 'Dados Coletados',
      icon: DataUsageIcon,
      content: `Coletamos diferentes tipos de informações para fornecer e melhorar nossos serviços:

**Dados de Identificação:**
- Nome completo, e-mail, telefone, data de nascimento
- Documentos de identificação (quando necessário)
- Informações de perfil e preferências

**Dados de Localização:**
- Endereço residencial, cidade, estado
- Localização da paróquia ou instituição
- Informações geográficas para eventos

**Dados de Eventos e Participação:**
- Histórico de participação em eventos
- Preferências religiosas e comunitárias
- Informações sobre sacramentos e atividades

**Dados de Pagamento:**
- Informações de cartão de crédito/débito
- Histórico de transações
- Dados bancários (quando aplicável)

**Dados Técnicos:**
- Endereço IP, tipo de dispositivo, navegador
- Logs de acesso e uso da plataforma
- Cookies e tecnologias similares`
    },
    {
      id: 'uso-dados',
      title: 'Como Usamos Seus Dados',
      icon: PersonIcon,
      content: `Utilizamos suas informações pessoais para os seguintes propósitos:

**Prestação de Serviços:**
- Gerenciar sua conta e perfil
- Processar inscrições em eventos
- Facilitar pagamentos e transações
- Enviar confirmações e lembretes

**Comunicação:**
- Notificações sobre eventos e atividades
- Informações sobre horários de missas
- Comunicados da paróquia
- Suporte técnico e atendimento

**Melhorias e Personalização:**
- Personalizar sua experiência na plataforma
- Desenvolver novos recursos e funcionalidades
- Análises estatísticas e relatórios
- Pesquisas de satisfação

**Segurança e Conformidade:**
- Prevenir fraudes e atividades suspeitas
- Cumprir obrigações legais
- Proteger direitos e propriedade
- Resolver disputas`
    },
    {
      id: 'compartilhamento',
      title: 'Compartilhamento de Dados',
      icon: ShareIcon,
      content: `Não vendemos, alugamos ou comercializamos suas informações pessoais. Compartilhamos dados apenas nas seguintes situações:

**Com Paróquias e Organizadores:**
- Informações necessárias para participação em eventos
- Dados de contato para comunicação direta
- Histórico de participação (quando autorizado)

**Prestadores de Serviços:**
- Processadores de pagamento
- Serviços de e-mail e comunicação
- Provedores de hospedagem e tecnologia
- Serviços de análise e métricas

**Exigências Legais:**
- Cumprimento de ordens judiciais
- Investigações governamentais
- Proteção de direitos legais
- Situações de emergência

**Transferências Empresariais:**
- Fusões, aquisições ou reorganizações
- Sempre com manutenção das proteções de privacidade`
    },
    {
      id: 'armazenamento',
      title: 'Armazenamento e Segurança',
      icon: StorageIcon,
      content: `Implementamos medidas técnicas e organizacionais para proteger seus dados:

**Medidas de Segurança:**
- Criptografia de dados em trânsito e em repouso
- Controles de acesso rigorosos
- Monitoramento contínuo de segurança
- Backups regulares e seguros

**Localização dos Dados:**
- Servidores localizados no Brasil
- Conformidade com regulamentações locais
- Transferências internacionais apenas quando necessário
- Proteções adequadas para transferências

**Retenção de Dados:**
- Mantemos dados apenas pelo tempo necessário
- Exclusão automática após períodos definidos
- Possibilidade de exclusão antecipada mediante solicitação
- Logs de segurança mantidos conforme exigências legais

**Acesso Restrito:**
- Apenas funcionários autorizados têm acesso
- Treinamento regular em proteção de dados
- Contratos de confidencialidade
- Auditoria regular de acessos`
    },
    {
      id: 'direitos',
      title: 'Seus Direitos',
      icon: CheckCircleIcon,
      content: `Conforme a LGPD, você possui os seguintes direitos sobre seus dados pessoais:

**Direito de Acesso:**
- Confirmar a existência de tratamento
- Acessar seus dados pessoais
- Obter informações sobre o tratamento

**Direito de Correção:**
- Corrigir dados incompletos ou inexatos
- Atualizar informações desatualizadas
- Completar dados incompletos

**Direito de Exclusão:**
- Solicitar a exclusão de dados desnecessários
- Cancelar o tratamento quando baseado em consentimento
- Excluir dados tratados em desconformidade

**Direito de Portabilidade:**
- Receber dados em formato estruturado
- Transferir dados para outro fornecedor
- Facilitar a migração de serviços

**Direito de Oposição:**
- Opor-se ao tratamento em certas situações
- Revogar consentimento a qualquer momento
- Solicitar limitação do tratamento

**Como Exercer Seus Direitos:**
- Entre em contato através do e-mail: contato@meusacramento.online
- Identifique-se adequadamente
- Especifique claramente sua solicitação
- Responderemos em até 15 dias úteis`
    },
    {
      id: 'menores',
      title: 'Proteção de Menores',
      icon: ChildCareIcon,
      content: `Temos cuidado especial com dados de crianças e adolescentes:

**Coleta de Dados de Menores:**
- Consentimento específico dos pais ou responsáveis
- Coleta limitada ao estritamente necessário
- Finalidades relacionadas ao interesse superior da criança
- Verificação da idade quando aplicável

**Proteções Adicionais:**
- Medidas de segurança reforçadas
- Acesso restrito a dados de menores
- Políticas específicas de retenção
- Treinamento especializado da equipe

**Participação em Eventos:**
- Autorização expressa dos responsáveis
- Informações de contato de emergência
- Acompanhamento adequado durante atividades
- Comunicação direta com responsáveis

**Direitos dos Responsáveis:**
- Acesso aos dados do menor
- Correção e exclusão quando necessário
- Oposição ao tratamento
- Acompanhamento das atividades online`
    },
    {
      id: 'cookies',
      title: 'Cookies e Tecnologias',
      icon: CookieIcon,
      content: `Utilizamos cookies e tecnologias similares para melhorar sua experiência:

**Tipos de Cookies:**
- Cookies essenciais para funcionamento
- Cookies de preferências e configurações
- Cookies de análise e estatísticas
- Cookies de marketing (com consentimento)

**Finalidades:**
- Manter você conectado à plataforma
- Lembrar suas preferências
- Analisar uso e performance
- Personalizar conteúdo e anúncios

**Controle de Cookies:**
- Configurações do navegador
- Ferramentas de opt-out
- Preferências na plataforma
- Exclusão manual quando necessário

**Tecnologias Utilizadas:**
- Google Analytics (análise de uso)
- Pixels de conversão
- Web beacons
- Tecnologias de autenticação

**Gerenciamento:**
- Você pode desabilitar cookies no navegador
- Algumas funcionalidades podem ser limitadas
- Cookies essenciais não podem ser desabilitados
- Atualizamos esta seção conforme necessário`
    },
    {
      id: 'atualizacoes',
      title: 'Atualizações da Política',
      icon: UpdateIcon,
      content: `Esta Política de Privacidade pode ser atualizada periodicamente:

**Motivos para Atualizações:**
- Mudanças na legislação
- Novos recursos ou serviços
- Melhorias em segurança
- Feedback dos usuários

**Processo de Atualização:**
- Revisão regular da política
- Notificação sobre mudanças significativas
- Período de adaptação quando necessário
- Arquivo de versões anteriores

**Comunicação de Mudanças:**
- E-mail para usuários registrados
- Notificação na plataforma
- Destaque das principais alterações
- Data de vigência claramente indicada

**Sua Responsabilidade:**
- Revisar periodicamente esta política
- Entrar em contato em caso de dúvidas
- Atualizar suas preferências conforme necessário
- Continuar usando os serviços implica aceitação`
    },
    {
      id: 'contato',
      title: 'Contato e DPO',
      icon: ContactSupportIcon,
      content: `Para questões relacionadas à privacidade e proteção de dados:

**Encarregado de Proteção de Dados (DPO):**
- E-mail: dpo@meusacramento.online
- Responsável por questões de privacidade
- Ponto de contato com autoridades
- Orientação sobre direitos dos titulares

**Contato Geral:**
- E-mail: contato@meusacramento.online
- WhatsApp: +55 98 98545-5382
- Endereço: Santa Helena - MA, Brasil
- Horário: Segunda a sexta, 8h às 18h

**Autoridade Nacional de Proteção de Dados (ANPD):**
- Site: https://www.gov.br/anpd
- E-mail: comunicacao@anpd.gov.br
- Para reclamações e denúncias
- Recurso em caso de não resolução

**Tempo de Resposta:**
- Solicitações gerais: até 15 dias úteis
- Questões urgentes: até 72 horas
- Exercício de direitos: conforme LGPD
- Acompanhamento disponível por e-mail`
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              color: 'white',
              mb: 4,
              alignSelf: 'flex-start',
              '&:hover': { background: 'rgba(255,255,255,0.1)' }
            }}
          >
            Voltar ao Início
          </Button>

          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              mx: 'auto', 
              mb: 4,
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <PolicyIcon sx={{ fontSize: 40 }} />
          </Avatar>
          
          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            fontWeight="bold"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              mb: 3,
              textShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
          >
            Política de Privacidade
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4, 
              opacity: 0.95, 
              maxWidth: 700, 
              mx: 'auto',
              fontWeight: 300,
              lineHeight: 1.4
            }}
          >
            Transparência e proteção dos seus dados pessoais
          </Typography>
          <Chip 
            label="Última atualização: Dezembro 2024" 
            sx={{ 
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 'bold'
            }} 
          />
        </Container>
      </Box>

      {/* Informações Gerais */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            mb: 6,
            background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.05)} 0%, ${alpha('#60a5fa', 0.05)} 100%)`,
            border: '2px solid rgba(59, 130, 246, 0.1)',
            borderRadius: 3
          }}
        >
          <Box display="flex" alignItems="center" mb={3}>
            <SecurityIcon sx={{ fontSize: 40, color: '#3b82f6', mr: 2 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                ACUTIS TECNOLOGIA LTDA
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Comprometida com a proteção dos seus dados
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            Esta Política de Privacidade se aplica às plataformas <strong>Meu Sacramento</strong> e{' '}
            <strong>Meu Sacramento</strong>, desenvolvidas e operadas pela ACUTIS TECNOLOGIA LTDA,
            empresa brasileira sediada em Santa Helena - MA, especializada em soluções digitais 
            para comunidades e instituições religiosas.
          </Typography>
          
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            Estamos em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD)</strong> 
            e demais regulamentações aplicáveis, garantindo que seus dados pessoais sejam tratados 
            com o máximo cuidado, segurança e transparência.
          </Typography>
        </Paper>
      </Container>

      {/* Seções da Política */}
      <Container maxWidth="lg" sx={{ pb: 10 }}>
        <Box mb={6}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            textAlign="center"
            sx={{
              background: 'linear-gradient(45deg, #1f2937, #374151)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Detalhes da Política
          </Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center">
            Informações completas sobre como tratamos seus dados
          </Typography>
        </Box>

        <Stack spacing={3}>
          {sections.map((section, index) => (
            <Accordion 
              key={section.id}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '12px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.1)',
                  borderColor: '#3b82f6'
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#3b82f6' }} />}
                sx={{
                  py: 2,
                  px: 3,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  }
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 50, 
                    height: 50, 
                    mr: 3,
                    background: `linear-gradient(45deg, #3b82f6, #60a5fa)`
                  }}
                >
                  <section.icon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Seção {index + 1} de {sections.length}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Divider sx={{ mb: 3 }} />
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.8,
                    whiteSpace: 'pre-line'
                  }}
                >
                  {section.content}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Container>

      {/* Resumo dos Direitos */}
      <Box sx={{ background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.05)} 0%, ${alpha('#60a5fa', 0.05)} 100%)`, py: 8 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            textAlign="center"
            sx={{ mb: 6 }}
          >
            Resumo dos Seus Direitos
          </Typography>
          
          <Stack spacing={3}>
            {[
              { icon: PersonIcon, title: 'Acesso', desc: 'Confirmar e acessar seus dados pessoais' },
              { icon: UpdateIcon, title: 'Correção', desc: 'Corrigir dados incompletos ou inexatos' },
              { icon: DeleteIcon, title: 'Exclusão', desc: 'Solicitar a exclusão de dados desnecessários' },
              { icon: ShareIcon, title: 'Portabilidade', desc: 'Receber dados em formato estruturado' },
              { icon: SecurityIcon, title: 'Oposição', desc: 'Opor-se ao tratamento em certas situações' },
              { icon: ContactSupportIcon, title: 'Suporte', desc: 'Contatar nosso DPO para esclarecimentos' }
            ].map((right, index) => (
              <Paper
                key={right.title}
                sx={{
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(59, 130, 246, 0.1)',
                    borderColor: '#3b82f6'
                  }
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    mr: 3,
                    background: `linear-gradient(45deg, #3b82f6, #60a5fa)`
                  }}
                >
                  <right.icon sx={{ fontSize: 30 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {right.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {right.desc}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Call to Action */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 6, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
            color: 'white',
            borderRadius: 3
          }}
        >
          <ContactSupportIcon sx={{ fontSize: 80, mb: 3, opacity: 0.9 }} />
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
            Dúvidas sobre Privacidade?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
            Nossa equipe está pronta para esclarecer qualquer questão sobre 
            o tratamento dos seus dados pessoais
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => window.open('mailto:dpo@meusacramento.online', '_blank')}
              sx={{
                px: 4,
                py: 2,
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              Contatar DPO
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.open('https://wa.me/5598985455382', '_blank')}
              sx={{
                px: 4,
                py: 2,
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              WhatsApp
            </Button>
          </Stack>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ background: '#111827', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="body1" gutterBottom>
              <strong>ACUTIS TECNOLOGIA LTDA</strong>
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ mb: 3 }}>
              Santa Helena - MA, Brasil | contato@meusacramento.online
            </Typography>
            
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
            
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              © 2024 ACUTIS TECNOLOGIA LTDA - Todos os direitos reservados
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.5)" sx={{ mt: 1 }}>
              Esta Política de Privacidade está em conformidade com a LGPD (Lei nº 13.709/2018)
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PoliticaPrivacidadePage;

